import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NextUI } from '@/components/NextUI';
import { useAppDispatch } from '@/hooks/redux';
import { showModal } from '@/store/slices/modalSlice';
import Icon from '@/components/Common/Icons/Icon';
import clsx from 'clsx';
import usePaddlePricing from '@/features/subscription/hooks/usePaddlePricing';
import { useAppSelector } from '@/hooks/redux';
import FilePreviewPanel from './FilePreviewPanel';
import Link from 'next/link';
import {
  COMMON_EVENTS,
  getUserData,
  logHubxEvent,
} from '@/app/providers/analytics';
import { analytics } from '@/lib/firebaseConfig';
import { logEvent } from 'firebase/analytics';
import useAuth from '@/hooks/useAuth';
import { CloseIcon } from '@heroui/shared-icons';
import { resolveProcessPreviewExtension } from '../../utils/resolveProcessPreviewExtension';
import { RootState } from '@/store';
import { resolveAnalyticsPaywallType } from '@/features/subscription/utils/analyticsPaywall';
import { useSingleTrigger } from '@hubxco/hubx-smartlink';
import { formatAnalyticsSource } from '@/helpers/common';
import { buildStructuredAnalyticsSource } from '@/lib/analytics/utils/source';
import {
  getAnnualPaywallPrice,
  getDisplayPlanName,
  getImmediateChargeAmount,
  getImmediateChargeCurrency,
  getIntervalAdjective,
  getPriceInterval,
  getPricePeriodLabel,
  getPrimaryPaywallPrice,
  getRecurringChargeAmount,
  getRecurringChargeCurrency,
  getRecurringPriceForSelectedPrice,
  getSelectedPaywallPrice,
  getTrialDurationText,
  getTrialPricePeriodLabel,
  hasTrialPeriod,
  shouldUseTrialPricing,
} from '@/features/subscription/utils/pricing/paywallPricing';
import { Document, Page, pdfjs } from 'react-pdf';
import {
  getPreviewBadgeColor,
  getPreviewFileType,
  getPreviewIconName,
} from './planSelectionPreview';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const BARLOW_FONT = {
  fontFamily: 'Barlow, -apple-system, BlinkMacSystemFont, sans-serif',
};

const SF_PRO_FONT = {
  fontFamily: '"SF Pro Display", "SF Pro Text", -apple-system, sans-serif',
};

const MobileRadio = ({ checked }: { checked: boolean }) => (
  <span
    className={clsx(
      'flex h-4 w-4 items-center justify-center rounded-full border transition-colors',
      checked ? 'border-[#4B7EFF]' : 'border-[#CECFD4]'
    )}
  >
    {checked && <span className="h-2 w-2 rounded-full bg-[#4B7EFF]" />}
  </span>
);

const MobilePreviewCard = ({
  file,
  processedFile,
  extension,
}: {
  file: File | null;
  processedFile: File | null;
  extension: string;
}) => {
  const fileToDisplay = processedFile || file;
  const pdfPreviewRef = useRef<HTMLDivElement | null>(null);
  const [pdfPreviewWidth, setPdfPreviewWidth] = useState(284);
  const imageUrl = useMemo(() => {
    if (!fileToDisplay || !fileToDisplay.type.startsWith('image/')) {
      return '';
    }
    return URL.createObjectURL(fileToDisplay);
  }, [fileToDisplay]);

  useEffect(() => {
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  useEffect(() => {
    if (!pdfPreviewRef.current || typeof ResizeObserver === 'undefined') {
      return;
    }

    const element = pdfPreviewRef.current;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      setPdfPreviewWidth(entry.contentRect.width);
    });

    observer.observe(element);
    setPdfPreviewWidth(element.clientWidth);

    return () => observer.disconnect();
  }, []);

  if (!fileToDisplay) {
    return null;
  }

  const fileType = getPreviewFileType(extension);
  const badgeColor = getPreviewBadgeColor(fileType);
  const isPdf = fileToDisplay.type === 'application/pdf';
  const isImage = fileToDisplay.type.startsWith('image/');

  return (
    <div className="w-full rounded-[0.625rem] border border-[#ECECEC] bg-white p-[0.875rem]">
      <div className="rounded-[0.625rem] border border-[#ECECEC] bg-[#E3FFF6] px-[0.875rem] py-[0.4375rem]">
        <div className="flex items-center justify-between gap-[0.75rem]">
          <div className="flex min-w-0 flex-1 items-center gap-[0.5rem]">
            <Icon
              name="PaywallFeatureCheck"
              width={16}
              height={16}
              className="shrink-0"
            />
            <span
              className="whitespace-nowrap text-[0.875rem] font-medium leading-normal text-[#292A33]"
              style={SF_PRO_FONT}
            >
              Your document is ready!
            </span>
          </div>
          <span
            className={clsx(
              'flex h-[1.75rem] shrink-0 items-center justify-center rounded-[0.375rem] px-[0.875rem] text-[0.75rem] font-semibold uppercase leading-none text-white',
              badgeColor
            )}
            style={SF_PRO_FONT}
          >
            {fileType}
          </span>
        </div>
      </div>

      <div className="mt-[0.5rem] rounded-[1rem] border-[0.0514375rem] border-[#EBECF1] bg-white shadow-[0_0_0.6170625rem_0_rgba(0,0,0,0.12)]">
        <div
          ref={pdfPreviewRef}
          className="flex h-[24.6875rem] items-center justify-center overflow-hidden rounded-[1rem]"
        >
          {isPdf && (
            <Document
              file={fileToDisplay}
              loading={<NextUI.Spinner />}
              error={
                <div className="text-sm text-[#787887]">
                  Preview unavailable
                </div>
              }
            >
              <Page
                pageNumber={1}
                width={Math.max(Math.floor(pdfPreviewWidth), 1)}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Document>
          )}

          {isImage && (
            <img
              src={imageUrl}
              alt={fileToDisplay.name}
              className="h-full w-full object-contain"
            />
          )}

          {!isPdf && !isImage && (
            <div className="flex flex-col items-center gap-[1rem] px-[1.5rem] text-center">
              <div className="flex h-[5rem] w-[5rem] items-center justify-center rounded-[1.25rem] border border-[#EBECF1] bg-white shadow-sm">
                <Icon
                  name={getPreviewIconName(fileType)}
                  width={42}
                  height={42}
                />
              </div>
              <span
                className="text-[0.875rem] leading-[1.3] text-[#787887]"
                style={SF_PRO_FONT}
              >
                Preview ready. Continue to unlock your file.
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const BillingNotice = ({
  amountText,
  recurringAmountText,
  recurringBillingLabel,
  selectedPrice,
  hasActiveTrial,
  className,
}: {
  amountText: string;
  recurringAmountText: string;
  recurringBillingLabel: string;
  selectedPrice?: PaddlePrice;
  hasActiveTrial: boolean;
  className?: string;
}) => {
  const trialDurationText = getTrialDurationText(selectedPrice);

  return (
    <p className={clsx('text-[#6D6D6D]', className)} style={SF_PRO_FONT}>
      {hasActiveTrial ? (
        <>
          You will be charged {amountText} today. After your {trialDurationText}{' '}
          trial, you will be billed {recurringAmountText}{' '}
          {recurringBillingLabel} unless you cancel before renewal.
        </>
      ) : (
        <>
          You will be charged {amountText} upon purchase and automatically
          billed {recurringBillingLabel} unless you cancel at least 24 hours
          before the end of the current billing period.
        </>
      )}{' '}
      See our{' '}
      <Link
        href="https://pdfgenius.com/terms"
        target="_blank"
        className="underline underline-offset-2"
      >
        Terms of Use
      </Link>{' '}
      for details on cancellation and refunds. We provide refunds in accordance
      with our{' '}
      <Link
        href="https://pdfgenius.com/refundpolicy"
        target="_blank"
        className="underline underline-offset-2"
      >
        Refund Policy
      </Link>
      .
    </p>
  );
};

const PlanSelectionModal = ({
  data,
  hideModal,
}: {
  data?: any;
  hideModal: () => void;
}) => {
  const [selectedPlan, setSelectedPlan] = useState<'weekly' | 'annual'>(
    'weekly'
  );
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const typeForUserAccessLevel = useAppSelector(
    (state: RootState) => state.userAccess.typeForUserAccessLevel
  );
  const {
    prices,
    monthlyPrices,
    weeklyPrices,
    yearlyPrices,
    hadWeeklyDiscount,
    isLoadingPrices,
    pricesError,
  } = usePaddlePricing();
  const { files, exportAsFiles } = useAppSelector((state: any) => state.tool);
  const hasLoggedPaywallSeenRef = React.useRef(false);
  const previewFile = (files ?? [])[0] ?? data?.file ?? null;
  const previewProcessedFile =
    (exportAsFiles ?? [])[0] ?? data?.processedFile ?? null;
  const previewExtension = resolveProcessPreviewExtension({
    extension: data?.extension,
    toolType: data?.toolType,
    file: previewFile,
    processedFile: previewProcessedFile,
  });
  const paywallType = resolveAnalyticsPaywallType({
    paywallType: typeForUserAccessLevel,
    toolType: data?.toolType,
  });
  const toolSource = data?.toolType
    ? formatAnalyticsSource(data.toolType)
    : undefined;
  const analyticsSource = data?.toolType
    ? buildStructuredAnalyticsSource(data.toolType, data?.originEntryPoint)
    : undefined;
  const formatPrice = (
    amount: number | undefined,
    currency: string = 'USD'
  ) => {
    if (amount === undefined) return '...';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const getDisplayAmount = (price?: PaddlePrice) => {
    if (!price) return undefined;

    const immediateChargeAmount = getImmediateChargeAmount(price);
    const recurringAmount = getRecurringChargeAmount(price);
    const discountAmount = Number(price.custom_data?.discountAmount ?? 0);
    const shouldApplyWeeklyDiscount =
      getPriceInterval(price) === 'week' &&
      !hasTrialPeriod(price) &&
      !hadWeeklyDiscount &&
      discountAmount > 0;

    if (shouldUseTrialPricing({ price, user })) {
      return immediateChargeAmount;
    }

    if (shouldApplyWeeklyDiscount) {
      return recurringAmount - discountAmount;
    }

    return recurringAmount;
  };

  const getDisplayCurrency = (price?: PaddlePrice) => {
    if (!price) return 'USD';

    return shouldUseTrialPricing({ price, user })
      ? getImmediateChargeCurrency(price)
      : getRecurringChargeCurrency(price);
  };

  const primaryPlanPrice = getPrimaryPaywallPrice({
    monthlyPrices,
    weeklyPrices,
    user,
  });
  const annualPlanPrice = getAnnualPaywallPrice(yearlyPrices);
  const hasAnnualPlan = annualPlanPrice != null;
  const effectiveSelectedPlan =
    selectedPlan === 'annual' && !hasAnnualPlan ? 'weekly' : selectedPlan;
  const selectedRawPrice = getSelectedPaywallPrice({
    selectedPlan: effectiveSelectedPlan,
    monthlyPrices,
    weeklyPrices,
    yearlyPrices,
    user,
  });
  const selectedRecurringPrice = getRecurringPriceForSelectedPrice({
    selectedPrice: selectedRawPrice,
    monthlyPrices,
    weeklyPrices,
    yearlyPrices,
  });
  const selectedAmount = getDisplayAmount(selectedRawPrice);
  const selectedAmountText = formatPrice(
    selectedAmount,
    getDisplayCurrency(selectedRawPrice)
  );
  const selectedRecurringAmountText = formatPrice(
    getRecurringChargeAmount(selectedRecurringPrice),
    getRecurringChargeCurrency(selectedRecurringPrice)
  );
  const selectedHasActiveTrial = shouldUseTrialPricing({
    price: selectedRawPrice,
    user,
  });
  const recurringBillingLabel = getIntervalAdjective(
    getPriceInterval(selectedRecurringPrice)
  );

  const plans = {
    weekly: {
      id: 'weekly',
      name: getDisplayPlanName(primaryPlanPrice, 'PDF Genius Pro'),
      price: formatPrice(
        getDisplayAmount(primaryPlanPrice),
        getDisplayCurrency(primaryPlanPrice)
      ),
      period: shouldUseTrialPricing({ price: primaryPlanPrice, user })
        ? getTrialPricePeriodLabel(primaryPlanPrice, 'week')
        : getPricePeriodLabel(primaryPlanPrice, 'week'),
      badge: 'Most Popular',
    },
    annual: {
      id: 'annual',
      name: getDisplayPlanName(annualPlanPrice, 'Annual Plan'),
      price: formatPrice(
        getDisplayAmount(annualPlanPrice),
        getDisplayCurrency(annualPlanPrice)
      ),
      period: getPricePeriodLabel(annualPlanPrice, 'year'),
    },
  };

  useEffect(() => {
    if (selectedPlan === 'annual' && !hasAnnualPlan) {
      setSelectedPlan('weekly');
    }
  }, [hasAnnualPlan, selectedPlan]);

  const features = [
    'Unlimited edits',
    'Unlimited downloads',
    'Multi-format conversion',
    'Edit text and images in PDF files',
    'Organize and reorder PDF pages',
    'Use PDF Genius on mobile',
  ];

  useSingleTrigger(() => {
    const analyticsUserData = getUserData(user);

    logHubxEvent({
      eventName: COMMON_EVENTS.LND_PAYWALL,
      ...(toolSource ? { source: toolSource } : {}),
      event_params: {
        ...(analyticsSource ? { source: analyticsSource } : {}),
        paywallType,
      } as any,
    });

    if (analytics) {
      logEvent(analytics, 'lnd_paywall');
    }

    if (typeof window !== 'undefined' && (window as any).dataLayer) {
      const dataLayerPayload = {
        event: 'lnd_paywall_v2',
        internal_use: false,
        user_data: analyticsUserData,
      };

      (window as any).dataLayer.push(dataLayerPayload);
    }
  });

  React.useEffect(() => {
    if (hasLoggedPaywallSeenRef.current) {
      return;
    }

    if (isLoadingPrices || pricesError || !prices) {
      return;
    }

    hasLoggedPaywallSeenRef.current = true;
    logHubxEvent({
      eventName: COMMON_EVENTS.PAYWALL_SEEN,
      event_params: {
        paywallType,
      },
    });
  }, [isLoadingPrices, pricesError, prices, data?.toolType]);

  const handleCloseClick = () => {
    logHubxEvent({
      eventName: COMMON_EVENTS.BTN_PAYWALL_CLOSE,
      event_params: {
        paywallType,
      },
    });
    data?.onCancel?.();
    hideModal();
  };

  const handleDismiss = () => {
    logHubxEvent({
      eventName: COMMON_EVENTS.BTN_PAYWALL_CLOSE,
      event_params: {
        paywallType,
      },
    });
    data?.onCancel?.();
    hideModal();
  };

  const handleContinue = () => {
    dispatch(
      showModal({
        modalKey: 'PaddlePayment',
        modalData: { ...data, selectedPlan: effectiveSelectedPlan },
      })
    );
  };

  return (
    <NextUI.Modal
      className="xl:max-w-[1340px] max-w-[1000px] max-lg:!m-0 max-lg:!mx-0 max-lg:h-[100dvh] max-lg:max-h-[100dvh] max-lg:w-screen max-lg:max-w-none w-[95vw] max-h-[95vh] !my-0 !mx-[20px]"
      classNames={{
        content:
          'p-6 xl:p-7 !overflow-y-auto max-lg:h-[100dvh] max-lg:max-h-[100dvh] max-lg:overflow-y-auto max-lg:rounded-none max-lg:bg-transparent max-lg:p-0 max-lg:shadow-none',
        closeButton: 'top-10 right-10 text-[1.25rem]',
      }}
      defaultOpen={true}
      name={'PlanSelection'}
      backdrop="blur"
      scrollBehavior="inside"
      hideCloseButton={true}
      onClose={handleDismiss}
    >
      <>
        <div className="lg:hidden relative min-h-[100dvh] w-screen overflow-x-hidden">
          <div className="mx-auto min-h-[100dvh] w-full max-w-[24.375rem] pb-[9.5rem] pt-[3.75rem]">
            <div className="mx-auto w-[21.75rem] max-w-[calc(100vw-2.625rem)] rounded-[1rem] bg-white px-[0.875rem] py-[0.875rem]">
              <div className="flex items-start justify-between gap-3">
                <h2
                  className="pr-[0.5rem] text-[1.125rem] font-semibold leading-[1.222222] text-[#292A33] whitespace-nowrap"
                  style={BARLOW_FONT}
                >
                  Choose a plan to download your file
                </h2>
                <button
                  type="button"
                  aria-label="Close"
                  onClick={handleCloseClick}
                  className="mt-0.5 flex h-4 w-4 items-center justify-center text-[#787887]"
                >
                  <CloseIcon />
                </button>
              </div>

              <div className="mt-[0.75rem] flex flex-col gap-[0.5rem]">
                <button
                  type="button"
                  onClick={() => setSelectedPlan('weekly')}
                  className="w-full rounded-[0.625rem] border border-[#ECECEC] bg-white px-[0.875rem] py-[0.875rem] text-left"
                >
                  <div className="flex min-h-[1.3125rem] items-center justify-between gap-[0.75rem]">
                    <div className="flex min-w-0 flex-1 items-center gap-[0.5rem]">
                      <MobileRadio
                        checked={effectiveSelectedPlan === 'weekly'}
                      />
                      <span
                        className="whitespace-nowrap text-[0.875rem] font-medium leading-normal text-black"
                        style={SF_PRO_FONT}
                      >
                        {plans.weekly.name}
                      </span>
                      <span
                        className="flex h-[1.25rem] w-[4.5rem] items-center justify-center rounded-[0.375rem] border border-[#BFA6FF] bg-[#EBE3FF] text-[0.625rem] font-medium leading-none text-[#7F4DFB]"
                        style={SF_PRO_FONT}
                      >
                        Most Popular
                      </span>
                    </div>
                    <span className="shrink-0 text-[0px] whitespace-nowrap">
                      <span
                        className="text-[1rem] font-bold leading-normal text-black"
                        style={SF_PRO_FONT}
                      >
                        {plans.weekly.price}
                      </span>
                      <span
                        className="text-[1rem] font-medium leading-normal text-[#787887]"
                        style={SF_PRO_FONT}
                      >
                        {plans.weekly.period}
                      </span>
                    </span>
                  </div>

                  {effectiveSelectedPlan === 'weekly' ? (
                    <div className="mt-[0.75rem] rounded-[0.625rem] border border-[#ECECEC] px-[0.875rem] pt-[0.875rem]">
                      <p
                        className="text-[1rem] font-medium leading-normal text-[#454655]"
                        style={SF_PRO_FONT}
                      >
                        Features
                      </p>
                      <div className="mt-[0.75rem]">
                        {features.map((feature, i) => (
                          <div
                            key={feature}
                            className={clsx(
                              'flex min-h-[2.5rem] items-center gap-[0.5rem] py-[0.75rem]',
                              i !== features.length - 1 &&
                                'border-b border-[#ECECEC]'
                            )}
                          >
                            <Icon
                              name="PaywallFeatureCheck"
                              width={16}
                              height={16}
                              className="shrink-0"
                            />
                            <span
                              className="text-[0.875rem] leading-normal text-[#454655]"
                              style={SF_PRO_FONT}
                            >
                              {feature}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </button>

                {hasAnnualPlan ? (
                  <button
                    type="button"
                    onClick={() => setSelectedPlan('annual')}
                    className="w-full rounded-[0.625rem] border border-[#ECECEC] bg-white px-[0.875rem] py-[0.75rem] text-left"
                  >
                    <div className="flex min-h-[1.1875rem] items-center justify-between gap-[0.75rem]">
                      <div className="flex min-w-0 flex-1 items-center gap-[0.75rem]">
                        <MobileRadio
                          checked={effectiveSelectedPlan === 'annual'}
                        />
                        <span
                          className="whitespace-nowrap text-[0.875rem] font-medium leading-normal text-black"
                          style={SF_PRO_FONT}
                        >
                          {plans.annual.name}
                        </span>
                      </div>
                      <span className="shrink-0 text-[0px] whitespace-nowrap">
                        <span
                          className="text-[1rem] font-bold leading-normal text-black"
                          style={SF_PRO_FONT}
                        >
                          {plans.annual.price}
                        </span>
                        <span
                          className="text-[1rem] font-medium leading-normal text-[#787887]"
                          style={SF_PRO_FONT}
                        >
                          {plans.annual.period}
                        </span>
                      </span>
                    </div>
                    {effectiveSelectedPlan === 'annual' ? (
                      <div className="mt-[0.75rem] rounded-[0.625rem] border border-[#ECECEC] px-[0.875rem] pt-[0.875rem]">
                        <p
                          className="text-[1rem] font-medium leading-normal text-[#454655]"
                          style={SF_PRO_FONT}
                        >
                          Features
                        </p>
                        <div className="mt-[0.75rem]">
                          {features.map((feature, i) => (
                            <div
                              key={feature}
                              className={clsx(
                                'flex min-h-[2.5rem] items-center gap-[0.5rem] py-[0.75rem]',
                                i !== features.length - 1 &&
                                  'border-b border-[#ECECEC]'
                              )}
                            >
                              <Icon
                                name="PaywallFeatureCheck"
                                width={16}
                                height={16}
                                className="shrink-0"
                              />
                              <span
                                className="text-[0.875rem] leading-normal text-[#454655]"
                                style={SF_PRO_FONT}
                              >
                                {feature}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </button>
                ) : null}

                {!data?.isOnlyPayment ? (
                  <MobilePreviewCard
                    file={previewFile}
                    processedFile={previewProcessedFile}
                    extension={previewExtension}
                  />
                ) : null}
              </div>
            </div>
          </div>

          <div className="fixed inset-x-0 bottom-0 z-20 border-t border-[#ECECEC] bg-white/95 px-[1rem] pb-[1rem] pt-[0.625rem] backdrop-blur">
            <div className="mx-auto w-full max-w-[21.75rem]">
              <BillingNotice
                amountText={selectedAmountText}
                recurringAmountText={selectedRecurringAmountText}
                recurringBillingLabel={recurringBillingLabel}
                selectedPrice={selectedRawPrice}
                hasActiveTrial={selectedHasActiveTrial}
                className="text-center text-[0.625rem] leading-[1.35]"
              />
              <button
                type="button"
                onClick={handleContinue}
                className="mt-[0.625rem] flex h-[3rem] w-full items-center justify-center rounded-[0.3125rem] bg-[#4B7EFF] text-[1.0625rem] font-medium leading-none text-white"
                style={BARLOW_FONT}
              >
                Continue
              </button>
            </div>
          </div>
        </div>

        <div className="hidden flex-col bg-white lg:flex">
          <button
            type="button"
            aria-label="Close"
            onClick={handleCloseClick}
            className="absolute top-10 right-10 text-[1.25rem]"
          >
            <CloseIcon />
          </button>
          <div className="flex items-center justify-between pb-4">
            <h2 className="text-2xl font-bold text-dark">
              Choose a plan to download your file
            </h2>
          </div>

          {data?.isOnlyPayment ? (
            <div className="mb-5 flex items-center justify-center gap-3 text-sm font-medium">
              <div className="flex items-center gap-2 text-primary-blue">
                <span className="w-[25px] h-[25px] rounded-full bg-primary-blue text-white flex items-center justify-center text-[16px] text-medium">
                  1
                </span>
                <span className="text-[#6D6D6D] text-[17px]">Select plan</span>
              </div>
              <div className="w-12 h-[1px] bg-[#CACACA]"></div>
              <div className="flex items-center gap-2 text-[#6D6D6D]">
                <span className="text-[#292A33] border border-[#D3E6F4] w-[25px] h-[25px] rounded-full bg-[#F1F9FF] flex items-center justify-center text-[16px] text-medium">
                  2
                </span>
                <span className="text-[#6D6D6D] text-[17px]">
                  Payment details
                </span>
              </div>
              <div className="ml-auto">
                <NextUI.Button
                  className="bg-[#4B7EFF] hover:!bg-[#4B7EFF] text-white px-8 lg:px-14 py-[14px] rounded-[0.3125rem]"
                  onClick={handleContinue}
                >
                  Continue
                </NextUI.Button>
              </div>
            </div>
          ) : (
            <div className="mb-5 flex items-center justify-center gap-3 text-sm font-medium">
              <div className="flex items-center gap-2 text-primary-blue">
                <Icon name="PaywallStepComplete" width={25} height={25} />
                <span className="text-[#6D6D6D] text-[17px]">
                  Document is ready
                </span>
              </div>
              <div className="w-12 h-[1px] bg-[#CACACA]"></div>
              <div className="flex items-center gap-2 text-primary-blue">
                <span className="w-[25px] h-[25px] rounded-full bg-primary-blue text-white flex items-center justify-center text-[16px] text-medium">
                  2
                </span>
                <span className="text-[#6D6D6D] text-[17px]">Select plan</span>
              </div>
              <div className="w-12 h-[1px] bg-[#CACACA]"></div>
              <div className="flex items-center gap-2 text-[#6D6D6D]">
                <span className="text-[#292A33] border border-[#D3E6F4] w-[25px] h-[25px] rounded-full bg-[#F1F9FF] flex items-center justify-center text-[16px] text-medium">
                  3
                </span>
                <span className="text-[#6D6D6D] text-[17px]">
                  Payment details
                </span>
              </div>
              <div className="w-12 h-[1px] bg-[#CACACA]"></div>
              <div className="flex items-center gap-2 text-[#6D6D6D]">
                <span className="text-[#292A33] border border-[#D3E6F4] w-[25px] h-[25px] rounded-full bg-[#F1F9FF] flex items-center justify-center text-[16px] text-medium">
                  4
                </span>
                <span className="text-[#6D6D6D] text-[17px]">Download</span>
              </div>
              <div className="max-sm:mx-auto ml-auto">
                <NextUI.Button
                  className="bg-[#4B7EFF] hover:!bg-[#4B7EFF] text-white px-8 lg:px-14 py-[14px] rounded-[0.3125rem]"
                  onClick={handleContinue}
                >
                  Continue
                </NextUI.Button>
              </div>
            </div>
          )}

          <div
            className={clsx(
              'flex h-full flex-col-reverse justify-center gap-6 lg:flex-row',
              data?.isOnlyPayment && 'items-center'
            )}
          >
            {data?.isOnlyPayment ? (
              <></>
            ) : (
              <div className="flex w-full flex-col rounded-xl border border-[#ECECEC] p-6 lg:flex-1">
                <FilePreviewPanel
                  file={previewFile}
                  processedFile={previewProcessedFile}
                  compactLayout
                  extension={previewExtension}
                />
              </div>
            )}

            <div
              className={clsx(
                data?.isOnlyPayment && 'max-w-[500px]',
                'flex w-full flex-col space-y-3 lg:flex-1'
              )}
            >
              <div
                onClick={() => setSelectedPlan('weekly')}
                className={clsx(
                  'cursor-pointer rounded-xl border bg-white p-4 transition-all'
                )}
              >
                <div className="flex items-center gap-2.5">
                  <div
                    className={clsx(
                      'w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors',
                      effectiveSelectedPlan === 'weekly'
                        ? 'border-primary-blue bg-primary-blue'
                        : 'border-gray-300 bg-white'
                    )}
                  >
                    {effectiveSelectedPlan === 'weekly' && (
                      <div className="w-2 h-2 rounded-full bg-white" />
                    )}
                  </div>
                  <span className="text-lg font-medium leading-normal text-black">
                    {plans.weekly.name}
                  </span>
                  <span className="ml-0 flex items-center rounded-md border border-[#BFA6FF] bg-[#EBE3FF] px-3 py-1.5 text-[12px] font-medium leading-none text-[#7F4DFB]">
                    {plans.weekly.badge}
                  </span>
                  <div className="ml-auto text-right">
                    <span className="font-bold text-black text-[18px]">
                      {plans.weekly.price}
                    </span>
                    <span className="text-[#6D6D6D] text-[18px] font-medium">
                      {plans.weekly.period}
                    </span>
                  </div>
                </div>

                {effectiveSelectedPlan === 'weekly' && (
                  <div className="mt-4 animate-appearance-in space-y-3 rounded-[10px] border border-[#ECECEC] p-3 pb-0">
                    <span className="text-[16px] font-medium text-[#454655] mb-4">
                      Features
                    </span>
                    <div className="grid grid-cols-1">
                      {features.map((feature, i) => (
                        <div
                          key={feature}
                          className={clsx(
                            'flex items-center gap-2.5 py-3',
                            i !== features.length - 1 &&
                              'border-b border-[#ECECEC]'
                          )}
                        >
                          <Icon
                            name="PaywallFeatureCheck"
                            width={20}
                            height={20}
                            className="shrink-0"
                          />

                          <span className="text-[#454655] text-[16px] mb-[2px]">
                            {feature}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {hasAnnualPlan ? (
                <div
                  onClick={() => setSelectedPlan('annual')}
                  className={clsx(
                    'cursor-pointer rounded-xl border bg-white p-4 transition-all'
                  )}
                >
                  <div className="flex items-center gap-2.5">
                    <div
                      className={clsx(
                        'w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors',
                        effectiveSelectedPlan === 'annual'
                          ? 'border-primary-blue bg-primary-blue'
                          : 'border-gray-300 bg-white'
                      )}
                    >
                      {effectiveSelectedPlan === 'annual' && (
                        <div className="w-2 h-2 rounded-full bg-white" />
                      )}
                    </div>

                    <span className="text-lg font-medium leading-normal text-black">
                      {plans.annual.name}
                    </span>

                    <div className="ml-auto text-right">
                      <span className="font-bold text-black text-[18px]">
                        {plans.annual.price}
                      </span>
                      <span className="text-[#6D6D6D] text-[18px] font-medium">
                        {plans.annual.period}
                      </span>
                    </div>
                  </div>

                  {effectiveSelectedPlan === 'annual' && (
                    <div className="mt-4 animate-appearance-in space-y-3 rounded-[10px] border border-[#ECECEC] p-3 pb-0">
                      <span className="text-[16px] font-medium text-[#454655] mb-4">
                        Features
                      </span>

                      <div className="grid grid-cols-1">
                        {features.map((feature, i) => (
                          <div
                            key={feature}
                            className={clsx(
                              'flex items-center gap-2.5 py-3',
                              i !== features.length - 1 &&
                                'border-b border-[#ECECEC]'
                            )}
                          >
                            <Icon
                              name="PaywallFeatureCheck"
                              width={20}
                              height={20}
                              className="shrink-0"
                            />

                            <span className="text-[#454655] text-[16px] mb-[2px]">
                              {feature}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>

          <BillingNotice
            amountText={selectedAmountText}
            recurringAmountText={selectedRecurringAmountText}
            recurringBillingLabel={recurringBillingLabel}
            selectedPrice={selectedRawPrice}
            hasActiveTrial={selectedHasActiveTrial}
            className="mx-auto mt-5 max-w-[760px] text-center text-[11px] leading-[1.45]"
          />
        </div>
      </>
    </NextUI.Modal>
  );
};

export default PlanSelectionModal;
